/**
 * AI CONTEXT
 * Purpose: the readable body of one standout moment on a student profile.
 * Teacher note, generated parent sentence, and eval write-up stay labeled
 * and separate. Photos and audio render even when there is no note, so a
 * media-only story is not an empty card.
 * Used by: StudentStandoutMomentsTab, StudentTaJourneyTab.
 */
import { useState } from "react"

import Flex from "#blocks/Flex"
import { type StandoutMediaEntry } from "#customTypes/composedTypes/standoutTypes"
import { useStandoutMediaUrl } from "#hooks/newHooks/useStandoutMediaUrl"
import { COLORS } from "#styles/colors"

import {
  formatMediaDuration,
  standoutMomentContent,
} from "./studentProfileTabHelpers"

const LABEL_STYLE = { fontWeight: 600, fontSize: 11 } as const

const MomentPhoto = ({ entry }: { entry: StandoutMediaEntry }) => {
  const url = useStandoutMediaUrl(entry.storagePath)

  return (
    <div data-testid="profile-moment-photo" data-media-id={entry.id}>
      <Flex col gap={4}>
        {url ? (
          <img
            src={url}
            alt={entry.caption || "Class photo"}
            style={{ maxWidth: 160, maxHeight: 120, objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: 12, color: COLORS.GRAY6 }}>Photo</span>
        )}
        {entry.caption ? <div>{entry.caption}</div> : null}
      </Flex>
    </div>
  )
}

const MomentAudio = ({ entry }: { entry: StandoutMediaEntry }) => {
  const [heard, setHeard] = useState(false)
  const url = useStandoutMediaUrl(entry.storagePath)
  const duration = entry.durationSeconds
    ? formatMediaDuration(entry.durationSeconds)
    : ""

  return (
    <div
      data-testid="profile-moment-audio"
      data-media-id={entry.id}
      data-heard={heard ? "true" : "false"}
    >
      <Flex col gap={4}>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <audio
            controls
            preload="none"
            src={url ?? undefined}
            onPlay={() => setHeard(true)}
          />
          <span style={{ fontSize: 12, color: COLORS.GRAY6 }}>
            {heard ? "Heard" : "Unheard"}
            {duration ? ` · ${duration}` : ""}
          </span>
        </Flex>
        {entry.caption ? <div>{entry.caption}</div> : null}
      </Flex>
    </div>
  )
}

const StandoutMomentContent = ({
  moment,
  omitKeys,
}: {
  moment: {
    note?: string | null
    generatedNote?: string | null
    generatedText?: string | null
    media?: StandoutMediaEntry[] | null
    assistantId?: string | null
  }
  omitKeys?: Array<"note" | "generatedNote" | "generatedText">
}) => {
  const content = standoutMomentContent(moment)
  const lines = omitKeys
    ? content.lines.filter((line) => !omitKeys.includes(line.key))
    : content.lines
  if (
    lines.length === 0 &&
    content.photos.length === 0 &&
    content.audio.length === 0
  ) {
    return null
  }

  return (
    <div data-testid="profile-moment-content">
      <Flex col gap={6}>
        {lines.map((line) => (
          <div
            key={line.key}
            data-testid={`profile-moment-${line.key}`}
            style={{ whiteSpace: "pre-wrap" }}
          >
            <span style={LABEL_STYLE}>{line.label}: </span>
            {line.text}
          </div>
        ))}
        {content.photos.map((entry) => (
          <MomentPhoto key={entry.id} entry={entry} />
        ))}
        {content.audio.map((entry) => (
          <MomentAudio key={entry.id} entry={entry} />
        ))}
      </Flex>
    </div>
  )
}

export default StandoutMomentContent
